if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          const layout = this.dataset.desktopLayout || '';
          if (layout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        this.initScrollIndicator();
        this.initPdpVideoAutoplay();
        this.playInitialActiveMedia();
      }

      getPdpMediaRail() {
        if (!this.elements.viewer) return null;
        return (
          this.elements.viewer.querySelector('.product__media-list--horizontal-overlap') ||
          this.elements.viewer.querySelector('.product__media-list')
        );
      }

      getMostVisibleRailSlide(rail) {
        if (!rail) return null;
        const slides = rail.querySelectorAll('.product__media-item, .slider__slide');
        const rootRect = rail.getBoundingClientRect();
        const mid = rootRect.left + rootRect.width * 0.35;
        let best = null;
        let bestDist = Infinity;
        slides.forEach((slide) => {
          const rect = slide.getBoundingClientRect();
          if (rect.right < rootRect.left || rect.left > rootRect.right) return;
          const dist = Math.abs(rect.left - mid);
          if (dist < bestDist) {
            bestDist = dist;
            best = slide;
          }
        });
        return best;
      }

      initPdpVideoAutoplay() {
        const rail = this.getPdpMediaRail();
        if (!rail) return;

        let scrollTimer;
        rail.addEventListener(
          'scroll',
          () => {
            window.clearTimeout(scrollTimer);
            scrollTimer = window.setTimeout(() => {
              const slide = this.getMostVisibleRailSlide(rail);
              if (slide) this.playActiveMedia(slide);
            }, 120);
          },
          { passive: true }
        );
      }

      playInitialActiveMedia() {
        if (!this.elements.viewer) return;
        window.setTimeout(() => {
          const rail = this.getPdpMediaRail();
          const active =
            this.elements.viewer.querySelector('.is-active') ||
            (rail && this.getMostVisibleRailSlide(rail));
          if (active) this.playActiveMedia(active);
        }, 300);
      }

      initScrollIndicator() {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const slider = viewer.querySelector('.product__media-list');
        const indicator = viewer.querySelector('[data-pdp-media-scroll-indicator]');
        const track = viewer.querySelector('[data-pdp-media-scroll-track]');
        const thumb = viewer.querySelector('[data-pdp-media-scroll-thumb]');

        if (!slider || !indicator || !track || !thumb) return;

        const update = () => {
          const maxScroll = slider.scrollWidth - slider.clientWidth;
          if (maxScroll <= 1) {
            indicator.classList.add('is-hidden');
            return;
          }

          indicator.classList.remove('is-hidden');
          const trackWidth = track.offsetWidth;
          const visibleRatio = slider.clientWidth / slider.scrollWidth;
          const thumbWidth = Math.max(32, Math.round(visibleRatio * trackWidth));
          const maxThumbLeft = Math.max(0, trackWidth - thumbWidth);
          const thumbLeft = maxScroll > 0 ? (slider.scrollLeft / maxScroll) * maxThumbLeft : 0;

          thumb.style.width = `${thumbWidth}px`;
          thumb.style.transform = `translateX(${thumbLeft}px)`;
        };

        slider.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update, { passive: true });

        if (typeof ResizeObserver !== 'undefined') {
          const resizeObserver = new ResizeObserver(update);
          resizeObserver.observe(slider);
          resizeObserver.observe(track);
        }

        update();
      }

      onSlideChanged(event) {
        const currentElement = event.detail && event.detail.currentElement;
        if (currentElement) this.playActiveMedia(currentElement);

        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        if (!this.elements.viewer) return;
        const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!activeMedia) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.prepend(activeMedia);
          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }
          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          const layout = this.dataset.desktopLayout || '';
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          } else if (layout === 'horizontal_overlap') {
            const rail = activeMedia.parentElement;
            const maxScroll = Math.max(0, rail.scrollWidth - rail.clientWidth);
            const left = Math.max(0, Math.min(activeMedia.offsetLeft, maxScroll));
            rail.scrollTo({ left, behavior: 'smooth' });
          } else if (layout === 'stacked') {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        });
        this.playActiveMedia(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          this.setActiveThumbnail(activeThumbnail);
          this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
        } else {
          const position = [...activeMedia.parentElement.children].indexOf(activeMedia) + 1;
          this.announceLiveRegion(activeMedia, String(position));
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image || !this.elements.liveRegion) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        if (typeof playDeferredMediaInSlide === 'function') {
          playDeferredMediaInSlide(activeItem);
        }
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}

function getMostVisibleSecondarySlide(rail) {
  if (!rail) return null;
  const slides = rail.querySelectorAll('.rocket-pdp-media-secondary__item');
  const rootRect = rail.getBoundingClientRect();
  const mid = rootRect.left + rootRect.width * 0.35;
  let best = null;
  let bestDist = Infinity;
  slides.forEach((slide) => {
    const rect = slide.getBoundingClientRect();
    if (rect.right < rootRect.left || rect.left > rootRect.right) return;
    const dist = Math.abs(rect.left - mid);
    if (dist < bestDist) {
      bestDist = dist;
      best = slide;
    }
  });
  return best;
}

function initPdpSecondaryGalleryVideos() {
  document.querySelectorAll('[data-pdp-media-secondary]').forEach((root) => {
    const rail = root.querySelector('.rocket-pdp-media-secondary__list');
    const slider = root.querySelector('slider-component');
    if (!rail) return;

    const syncVideo = () => {
      const slide = getMostVisibleSecondarySlide(rail);
      if (slide && typeof playDeferredMediaInSlide === 'function') {
        playDeferredMediaInSlide(slide);
      }
    };

    let scrollTimer;
    rail.addEventListener(
      'scroll',
      () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(syncVideo, 120);
      },
      { passive: true }
    );

    if (slider) {
      slider.addEventListener('slideChanged', (event) => {
        const current = event.detail && event.detail.currentElement;
        if (current && typeof playDeferredMediaInSlide === 'function') {
          playDeferredMediaInSlide(current);
        }
      });
    }

    window.setTimeout(syncVideo, 400);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initPdpSecondaryGalleryVideos);
} else {
  initPdpSecondaryGalleryVideos();
}
